import { useState } from "react";

import ContextualMenu from "components/contextualMenu/ContextualMenu";
import Modal from "components/modal/Modal";
import { CustomerDocument } from "../../../../models/customers/documents";
import { useRootStore } from "store";

type DocumentsTableRowProps = {
  dataItem: CustomerDocument;
};

type DocumentPreview = {
  filename: string;
  url: string;
};

export function DocumentsTableRow({ dataItem }: DocumentsTableRowProps) {
  const [documentPreview, setDocumentPreview] = useState<DocumentPreview>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const { addNotification } = useRootStore((state) => state.toastNotifications);

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(
      () => {
        addNotification("success", "Länken kopierad!");
      },
      () => {
        addNotification("error", "Misslyckades att kopiera länken.");
      }
    );
  };

  const downloadFile = async (fileURL: string, fileName: string) => {
    try {
      const response = await fetch(fileURL, {
        method: "GET",
        headers: {
          Accept: "application/pdf"
        }
      });

      if (!response.ok) {
        throw new Error("Error occured");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      addNotification("error", "Något gick fel.");
    }
  };

  const previewDocument = async (filename: string, url: string) => {
    setDocumentPreview({
      filename: filename,
      url: url
    });
  };

  const handleDismiss = () => {
    setIsLoading(false);
    setHasError(false);
    setDocumentPreview(undefined); // Reset document preview
  };

  const getCategory = (category: string) => {
    return category.toLowerCase() === "unknown" ? "Okänd" : decodeURI(category);
  };

  return (
    <>
      <tr className="documents-table-row">
        <td>
          {new Date(dataItem.startedAtIso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "numeric",
            day: "numeric"
          })}
        </td>
        <td>
          {new Date(dataItem.startedAtIso).toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit"
          })}
        </td>
        <td>
          {dataItem.documents.map((file, index) => (
            <div
              className={`${index === 0 ? "pb-3" : "py-3"} text-max-char-sm`}
              title={file.filename}
              key={index}
            >
              {index + 1}/{dataItem.documents.length} {file.filename}
            </div>
          ))}
        </td>
        <td>
          {dataItem.documents.map((file, index) => (
            <div
              className={`text-max-char-lg ${index === 0 ? "pb-3" : "py-3"}`}
              title={file.key}
              key={index}
            >
              {file.key}
            </div>
          ))}
        </td>
        <td>
          {dataItem.documents.map((file, index) => (
            <div
              className={`${index === 0 ? "pb-3" : "py-3"} text-max-char-sm`}
              key={file.documentId + file.category}
              title={getCategory(file.category)}
            >
              {getCategory(file.category)}
            </div>
          ))}
        </td>
        <td title={dataItem.from}>
          <div className="text-max-char-sm" title={dataItem.from}>
            {dataItem.from}
          </div>
        </td>
        <td>
          {dataItem.documents.map((file) => (
            <div className="pb-3" key={file.documentId}>
              <ContextualMenu
                buttonIconName="ellipsis-h"
                menuItems={[
                  {
                    label: "Kopiera länk",
                    onClick: () => {
                      copyToClipboard(file.url);
                    },
                    iconName: "link"
                  },
                  {
                    label: "Förhandsgranska fil",
                    onClick: () => previewDocument(file.key, file.url),
                    iconName: "search-plus"
                  },
                  {
                    label: "Ladda ner fil",
                    onClick: () => downloadFile(file.url, file.key),
                    iconName: "download"
                  }
                ]}
              />
            </div>
          ))}
        </td>
      </tr>
      {documentPreview && (
        <Modal onDismiss={handleDismiss} modalTitle="document preview">
          <div>
            <h3>{documentPreview?.filename}</h3>

            {/* Loading Spinner */}
            {isLoading && <div>Hämtar dokument...</div>}

            {/* Error Message */}
            {hasError && (
              <div>Misslyckades att visa dokument. Vänligen försök igen.</div>
            )}

            {/* iFrame for document preview */}
            {!hasError && (
              <iframe
                src={documentPreview.url}
                title={documentPreview.filename}
                className="document-preview"
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setHasError(true);
                }}
                style={{ width: "100%", height: "500px" }}
              ></iframe>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
